import { zodResolver } from "@hookform/resolvers/zod";
import { t } from "@lingui/macro";
import { Check, CheckCircle, CircleNotch, DownloadSimple } from "@phosphor-icons/react";
import type { JsonResume, LinkedIn, PDFData, ReactiveResumeV3 } from "@stylingcv/parser";
import {
  JsonResumeParser,
  LinkedInParser,
  PDFParser,
  ReactiveResumeParser,
  ReactiveResumeV3Parser,
} from "@stylingcv/parser";
import type { ResumeData } from "@stylingcv/schema";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Label,
  ScrollArea,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@stylingcv/ui";
import { AnimatePresence } from "framer-motion";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { z, ZodError } from "zod";

import { useToast } from "@/client/hooks/use-toast";
import { useImportResume } from "@/client/services/resume/import";
import { useDialog } from "@/client/stores/dialog";

import { NextStepsModal } from "@/client/pages/smart-generator/components/next-steps-modal";

enum ImportType {
  "reactive-resume-json" = "reactive-resume-json",
  "reactive-resume-v3-json" = "reactive-resume-v3-json",
  "json-resume-json" = "json-resume-json",
  "linkedin-data-export-zip" = "linkedin-data-export-zip",
  "pdf-resume" = "pdf-resume",
}

const formSchema = z.object({
  file: z.instanceof(File),
  type: z.nativeEnum(ImportType),
});

type FormValues = z.infer<typeof formSchema>;

type ValidationResult =
  | {
      isValid: false;
      errors: string;
    }
  | {
      isValid: true;
      type: ImportType;
      result: ResumeData | ReactiveResumeV3 | LinkedIn | JsonResume | PDFData;
    };

type ImportProgress = {
  stage: "idle" | "reading" | "parsing" | "processing" | "extracting" | "saving" | "complete";
  message: string;
  percentage: number;
};

// Helper function to get resume title from headline or fallback to default
const getResumeTitle = (resumeData: any, fallbackTitle: string): string => {
  const headline = resumeData?.basics?.headline?.trim();
  return headline && headline.length > 0 ? headline : fallbackTitle;
};

export const ImportDialog = () => {
  const { toast } = useToast();
  const { isOpen, close } = useDialog("import");
  const { importResume, loading } = useImportResume();

  const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importProgress, setImportProgress] = useState<ImportProgress>({
    stage: "idle",
    message: "",
    percentage: 0,
  });

  const navigate = useNavigate();
  const [showNextStepsModal, setShowNextStepsModal] = useState(false);
  const [extractedPdfData, setExtractedPdfData] = useState<any>(null);

  const form = useForm<FormValues>({
    defaultValues: {
      type: ImportType["pdf-resume"],
    },
    resolver: zodResolver(formSchema),
  });
  const filetype = form.watch("type");

  useEffect(() => {
    if (isOpen) onReset();
  }, [isOpen]);

  useEffect(() => {
    form.reset({ file: undefined, type: filetype });
    setValidationResult(null);
  }, [filetype]);

  const accept = useMemo(() => {
    if (filetype.includes("json")) return ".json";
    if (filetype.includes("zip")) return ".zip";
    if (filetype === "pdf-resume") return ".pdf";
    return "";
  }, [filetype]);

  const onValidate = async () => {
    setIsValidating(true);
    try {
      const { file, type } = formSchema.parse(form.getValues());

      if (type === ImportType["reactive-resume-json"]) {
        const parser = new ReactiveResumeParser();
        const data = await parser.readFile(file);
        const result = parser.validate(data);

        setValidationResult({ isValid: true, type, result });
      }

      if (type === ImportType["reactive-resume-v3-json"]) {
        const parser = new ReactiveResumeV3Parser();
        const data = await parser.readFile(file);
        const result = parser.validate(data);

        setValidationResult({ isValid: true, type, result });
      }

      if (type === ImportType["json-resume-json"]) {
        const parser = new JsonResumeParser();
        const data = await parser.readFile(file);
        const result = parser.validate(data);

        setValidationResult({ isValid: true, type, result });
      }

      if (type === ImportType["linkedin-data-export-zip"]) {
        const parser = new LinkedInParser();
        const data = await parser.readFile(file);
        const result = await parser.validate(data);

        setValidationResult({ isValid: true, type, result });
      }

      if (type === ImportType["pdf-resume"]) {
        const parser = new PDFParser();
        const data = await parser.readFile(file);
        const result = parser.validate(data);

        setValidationResult({ isValid: true, type, result });
      }
    } catch (error) {
      if (error instanceof ZodError) {
        setValidationResult({
          isValid: false,
          errors: error.toString(),
        });

        toast({
          variant: "error",
          title: t`An error occurred while validating the file.`,
        });
      }
    } finally {
      setIsValidating(false);
    }
  };

  const onImport = async () => {
    const { type } = formSchema.parse(form.getValues());

    if (!validationResult?.isValid || validationResult.type !== type) return;

    // Start importing
    setIsImporting(true);
    setImportProgress({ stage: "reading", message: t`Reading file...`, percentage: 10 });

    try {
      if (type === ImportType["reactive-resume-json"]) {
        setImportProgress({ stage: "parsing", message: t`Parsing resume data...`, percentage: 30 });
        const parser = new ReactiveResumeParser();
        const data = parser.convert(validationResult.result as ResumeData);

        setImportProgress({
          stage: "saving",
          message: t`Saving to your account...`,
          percentage: 70,
        });
        await importResume({
          data,
          title: getResumeTitle(data, t`AI Generated Resume`),
          slug: "ai-generated-resume-" + Date.now(),
        });
        setImportProgress({ stage: "complete", message: t`Import complete!`, percentage: 100 });
      }

      if (type === ImportType["reactive-resume-v3-json"]) {
        setImportProgress({ stage: "parsing", message: t`Parsing resume data...`, percentage: 30 });
        const parser = new ReactiveResumeV3Parser();
        const data = parser.convert(validationResult.result as ReactiveResumeV3);

        setImportProgress({
          stage: "saving",
          message: t`Saving to your account...`,
          percentage: 70,
        });
        await importResume({
          data,
          title: "AI Generated Resume V3",
          slug: "ai-generated-resume-v3-" + Date.now(),
        });
        setImportProgress({ stage: "complete", message: t`Import complete!`, percentage: 100 });
      }

      if (type === ImportType["json-resume-json"]) {
        setImportProgress({ stage: "parsing", message: t`Parsing JSON Resume...`, percentage: 30 });
        const parser = new JsonResumeParser();
        const data = parser.convert(validationResult.result as JsonResume);

        setImportProgress({
          stage: "saving",
          message: t`Saving to your account...`,
          percentage: 70,
        });
        await importResume({ data });
        setImportProgress({ stage: "complete", message: t`Import complete!`, percentage: 100 });
      }

      if (type === ImportType["linkedin-data-export-zip"]) {
        setImportProgress({
          stage: "parsing",
          message: t`Parsing LinkedIn data...`,
          percentage: 30,
        });
        const parser = new LinkedInParser();
        const data = parser.convert(validationResult.result as LinkedIn);

        setImportProgress({
          stage: "saving",
          message: t`Saving to your account...`,
          percentage: 70,
        });
        await importResume({ data });
        setImportProgress({ stage: "complete", message: t`Import complete!`, percentage: 100 });
      }

      if (type === ImportType["pdf-resume"]) {
        // Extract text from PDF and send to AI for processing
        const pdfData = validationResult.result as PDFData;

        try {
          setImportProgress({
            stage: "extracting",
            message: t`Extracting text from PDF...`,
            percentage: 20,
          });

          // Call the AI extraction endpoint
          setImportProgress({
            stage: "processing",
            message: t`Processing with AI...`,
            percentage: 40,
          });
          const response = await fetch("/api/ai/resume/parse-pdf", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              pdfText: pdfData.text,
              language: "en-US",
            }),
          });

          if (!response.ok) {
            throw new Error("Failed to extract resume data from PDF");
          }

          setImportProgress({
            stage: "parsing",
            message: t`Parsing extracted data...`,
            percentage: 60,
          });
          const extractedData = await response.json();

          // Intercept for Next Steps Modal
          setExtractedPdfData(extractedData);
          setShowNextStepsModal(true);
          setIsImporting(false);
          setImportProgress({ stage: "idle", message: "", percentage: 0 });
          return;
        } catch (error) {
          setImportProgress({ stage: "idle", message: "", percentage: 0 });
          throw new Error(
            t`Failed to process PDF: ${error instanceof Error ? error.message : "Unknown error"}`,
          );
        }
      }

      // Wait a moment to show the complete message
      setTimeout(() => {
        setIsImporting(false);
        setImportProgress({ stage: "idle", message: "", percentage: 0 });
        close();
      }, 1500);
    } catch (error: unknown) {
      setIsImporting(false);
      setImportProgress({ stage: "idle", message: "", percentage: 0 });
      toast({
        variant: "error",
        title: t`Oops, the server returned an error.`,
        description: error instanceof Error ? error.message : undefined,
      });
    }
  };

  const onReset = () => {
    form.reset();
    setValidationResult(null);
    setIsValidating(false);
    setIsImporting(false);
    setImportProgress({ stage: "idle", message: "", percentage: 0 });
    setShowNextStepsModal(false);
    setExtractedPdfData(null);
  };

  const handleStartInterview = () => {
    if (!extractedPdfData) return;
    navigate("/interview", {
      state: {
        resumeData: extractedPdfData,
      },
    });
    close();
  };

  const handleSkipToBuilder = async () => {
    if (!extractedPdfData) return;

    setIsImporting(true);
    setImportProgress({ stage: "saving", message: t`Saving to your account...`, percentage: 80 });

    try {
      const createdResume = await importResume({
        data: extractedPdfData,
        title: getResumeTitle(extractedPdfData, t`Imported PDF Resume`),
        slug: "pdf-import-" + Date.now(),
      });

      setImportProgress({ stage: "complete", message: t`PDF import successful!`, percentage: 100 });

      setTimeout(() => {
        setIsImporting(false);
        setImportProgress({ stage: "idle", message: "", percentage: 0 });
        setShowNextStepsModal(false);
        close();
        navigate(`/builder/${createdResume.id}`);
      }, 1500);
    } catch {
      setIsImporting(false);
      setImportProgress({ stage: "idle", message: "", percentage: 0 });
      toast({
        variant: "error",
        title: t`Import Failed`,
        description: t`Failed to save your resume. Please try again.`,
      });
    }
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={close}>
        <DialogContent>
          <Form {...form}>
            <form className="space-y-4">
              <DialogHeader>
                <DialogTitle>
                  <div className="flex items-center space-x-2.5">
                    <DownloadSimple />
                    <h2>{t`Import an existing resume`}</h2>
                  </div>
                </DialogTitle>
                <DialogDescription>
                  {t`Upload a file from one of the accepted sources to parse existing data and import it into Stylion for easier editing.`}
                </DialogDescription>
              </DialogHeader>

              <FormField
                name="type"
                control={form.control}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t`Filetype`}</FormLabel>
                    <FormControl>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <SelectTrigger aria-label={t`Select file type`}>
                          <SelectValue placeholder={t`Please select a file type`} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="reactive-resume-json">Stylion (.json)</SelectItem>
                          <SelectItem value="linkedin-data-export-zip">
                            LinkedIn Data Export (.zip)
                          </SelectItem>
                          <SelectItem value="pdf-resume">PDF Resume (.pdf)</SelectItem>
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                name="file"
                control={form.control}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t`File`}</FormLabel>
                    <FormControl>
                      <Input
                        key={`${accept}-${filetype}`}
                        type="file"
                        accept={accept}
                        onChange={(event) => {
                          if (!event.target.files?.length) return;
                          field.onChange(event.target.files[0]);
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                    {accept && (
                      <FormDescription>
                        {t({
                          message: `Accepts only ${accept} files`,
                          comment:
                            "Helper text to let the user know what filetypes are accepted. {accept} can be .pdf or .json.",
                        })}
                      </FormDescription>
                    )}
                  </FormItem>
                )}
              />

              {validationResult?.isValid === false && (
                <div className="space-y-2">
                  <Label className="text-error">{t`Errors`}</Label>
                  <ScrollArea orientation="vertical" className="h-[180px]">
                    <div className="whitespace-pre-wrap rounded bg-secondary-accent p-4 font-mono text-xs leading-relaxed">
                      {JSON.stringify(JSON.parse(validationResult.errors), null, 4)}
                    </div>
                  </ScrollArea>
                </div>
              )}

              {/* Import Progress Indicator */}
              {isImporting && importProgress.stage !== "idle" && (
                <div className="space-y-3 rounded-lg border bg-muted/50 p-4">
                  <div className="flex items-center gap-3">
                    <CircleNotch className="size-5 animate-spin text-primary" />
                    <div className="flex-1">
                      <p className="text-sm font-medium">{importProgress.message}</p>
                      <p className="text-xs text-muted-foreground">
                        {importProgress.percentage}% complete
                      </p>
                    </div>
                    {importProgress.stage === "complete" && (
                      <CheckCircle className="size-5 text-green-500" />
                    )}
                  </div>

                  {/* Custom Progress Bar */}
                  <div className="w-full overflow-hidden rounded-full bg-secondary">
                    <div
                      className="h-2 bg-primary transition-all duration-300 ease-out"
                      style={{ width: `${importProgress.percentage}%` }}
                    />
                  </div>

                  {/* Stage indicators */}
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span
                      className={importProgress.percentage >= 20 ? "font-medium text-primary" : ""}
                    >
                      {filetype === "pdf-resume" ? t`Extract` : t`Read`}
                    </span>
                    <span
                      className={importProgress.percentage >= 40 ? "font-medium text-primary" : ""}
                    >
                      {filetype === "pdf-resume" ? t`Process AI` : t`Parse`}
                    </span>
                    <span
                      className={importProgress.percentage >= 70 ? "font-medium text-primary" : ""}
                    >
                      {t`Save`}
                    </span>
                    <span
                      className={
                        importProgress.percentage >= 100 ? "font-medium text-green-500" : ""
                      }
                    >
                      {t`Complete`}
                    </span>
                  </div>
                </div>
              )}

              <DialogFooter>
                <AnimatePresence presenceAffectsLayout>
                  {!validationResult && (
                    <Button
                      type="button"
                      disabled={isValidating || !form.watch("file")}
                      onClick={onValidate}
                    >
                      {isValidating ? (
                        <>
                          <CircleNotch className="mr-2 animate-spin" size={16} />
                          {t`Validating...`}
                        </>
                      ) : (
                        t`Validate`
                      )}
                    </Button>
                  )}

                  {validationResult !== null && !validationResult.isValid && (
                    <Button type="button" variant="secondary" onClick={onReset}>
                      {t`Discard`}
                    </Button>
                  )}

                  {validationResult !== null && validationResult.isValid && (
                    <>
                      <Button type="button" disabled={isImporting} onClick={onImport}>
                        {isImporting ? (
                          <>
                            <CircleNotch className="mr-2 animate-spin" size={16} />
                            {filetype === "pdf-resume" ? t`Processing PDF...` : t`Importing...`}
                          </>
                        ) : (
                          t`Import`
                        )}
                      </Button>

                      <Button disabled type="button" variant="success">
                        <Check size={16} weight="bold" className="me-2" />
                        {t`Validated`}
                      </Button>
                    </>
                  )}
                </AnimatePresence>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      <NextStepsModal
        isOpen={showNextStepsModal}
        jobRole={extractedPdfData?.basics?.label || t`Professional`}
        onClose={() => {
          setShowNextStepsModal(false);
        }}
        onStartInterview={handleStartInterview}
        onSkipToBuilder={handleSkipToBuilder}
      />
    </>
  );
};
